import { D } from './money';
import { computeTotals, positionKey } from './totals';

// Initial pick (AC-04, AC-10..AC-12, AC-16): sell the most losing positions
// first until the trading result drops to the target; the position where the
// needed loss is crossed gets the FIFO quantity that lands closest to it.

const nothing = (reason) => ({ selection: {}, short: false, reason });

const lotQty = (lot) => D(lot.quantity).abs();

// Piecewise-linear FIFO result (UAH) of selling q units of the position.
const fifoResult = (pos, q) => {
  let rest = q;
  let acc = D(0);
  for (const lot of pos.lots) {
    if (rest.lte(0)) break;
    const of = lotQty(lot);
    const part = rest.lt(of) ? rest : of;
    acc = acc.plus(D(lot.unrealizedResultUah).times(part).div(of));
    rest = rest.minus(part);
  }
  return acc;
};

// Quantity in [0, whole] on the 10^-scale grid whose result is closest to goal.
const closestQuantity = (pos, goal) => {
  const step = D(10).pow(-pos.quantityScale);
  const whole = D(pos.quantity).abs();
  const clamp = (q) => (q.lt(0) ? D(0) : q.gt(whole) ? whole : q);
  const grid = (q, mode) => clamp(q.div(step).toDecimalPlaces(0, mode).times(step));
  const FLOOR = 3;
  const CEIL = 2;

  const candidates = [D(0), whole];
  let start = D(0);
  let startResult = D(0);
  pos.lots.forEach((lot) => {
    const of = lotQty(lot);
    const end = start.plus(of);
    const resUah = D(lot.unrealizedResultUah);
    candidates.push(grid(start, FLOOR), grid(start, CEIL), grid(end, FLOOR), grid(end, CEIL));
    if (!resUah.isZero()) {
      const q = start.plus(D(goal).minus(startResult).times(of).div(resUah));
      if (q.gte(start) && q.lte(end)) candidates.push(grid(q, FLOOR), grid(q, CEIL));
    }
    start = end;
    startResult = startResult.plus(resUah);
  });

  let best = null;
  candidates.forEach((q) => {
    const dist = fifoResult(pos, q).minus(goal).abs();
    if (!best || dist.lt(best.dist) || (dist.eq(best.dist) && q.lt(best.q))) best = { q, dist };
  });
  return best.q;
};

export const initialPick = (snapshot, { loss, target, strategicKeys = [] }) => {
  const trading = snapshot.realized.buckets.find((b) => b.bucket === 'TRADING');
  const base = D(trading.income).minus(D(loss || 0));
  const targetD = D(target || 0);
  if (base.lte(0)) return nothing('ZERO_TRADING_TAX');
  if (base.lte(targetD)) return nothing('AT_OR_BELOW_TARGET');

  const strategic = new Set(strategicKeys);
  const candidates = snapshot.positions
    .filter(
      (p) =>
        p.price !== null &&
        p.unrealizedResultUah !== null &&
        D(p.unrealizedResultUah).lt(0) &&
        !strategic.has(positionKey(p)),
    )
    .sort((a, b) => D(a.unrealizedResultUah).cmp(D(b.unrealizedResultUah)));
  if (candidates.length === 0) return nothing('NO_LOSING_POSITIONS');

  const need = base.minus(targetD);
  const selection = {};
  let cumulative = D(0);
  let short = true;
  for (const pos of candidates) {
    const key = positionKey(pos);
    const whole = D(pos.quantity).abs();
    const posLoss = D(pos.unrealizedResultUah).neg();
    if (cumulative.plus(posLoss).lt(need)) {
      selection[key] = whole.toFixed();
      cumulative = cumulative.plus(posLoss);
    } else {
      const goal = targetD.minus(base.minus(cumulative));
      const q = closestQuantity(pos, goal);
      if (q.gt(0)) selection[key] = q.toFixed();
      short = false;
      break;
    }
  }

  const totals = computeTotals(snapshot, selection, loss);
  return {
    selection,
    short,
    summary: { count: Object.keys(selection).length, afterSale: totals.afterSale, target: targetD.toFixed() },
  };
};
